import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'

export default function useAreaList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItems, updateItems } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const areas = ref([])
  const collection = 'area'

  // Table Headers
  const tableColumns = computed(() => {
    return [
      { key: 'name', label: i18n.t('message.tableHeader.areas'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })

  const totalAreas = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchAreas = async (searchValue = '') => {
    isLoading.value = true

    try {

      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const items = await getItems({ collection, query })
      if (!items) return
  
      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      areas.value  = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalAreas.value = areas.value.length
    
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.physical_location_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchArea = searchValue => {
    isLoading.value = true
    fetchAreas(searchValue)
  }

  const deleteArea = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      const queryLocation = { client_id: oid(clientId), area: oid(id) }
      const actionLocation = { $set: { area: null } }
      await updateItems({ collection: 'location', query: queryLocation, action: actionLocation })

      fetchAreas()
      showSuccessMessage(i18n.t('message.area_location_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (areas.value.length ? 1 : 0)
    const to = areas.value.length + ((currentPage.value * perPage.value) - perPage.value)
    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalAreas.value })
  })

  watch([currentPage, perPage], () => {
    fetchAreas()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchAreas()
  })

  return {
    fetchAreas,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalAreas,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    areas,
    deleteArea,
    isLoading,
    searchArea
  }
}